import {
  BatchJobStatus,
  ImportJobImportType,
  ImportJobPipelineTargetSettingsInput,
  ImportJobSortOption,
  ImportJobTargetType,
  ImportPipelineConnectorSettingsFragment,
  ImportPipelineFragment,
  ImportPipelineSettingsInput,
  ImportSource,
  SortDirection,
  useCreateImportPipelineMutation,
  useLoadImportJobsQuery,
  useUpdateImportPipelineMutation,
} from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { formatDateTime } from '../../../common/formatHelper';
import { cloneWithoutTypename } from '../../../common/utils';
import ErrorIndicator from '../../../components/ErrorIndicator';
import { Button } from '../../../components/actions/Button';
import ButtonSwitchMulti from '../../../components/actions/ButtonSwitchMulti';
import DropdownSelector from '../../../components/actions/DropdownSelector';
import CodeEditor from '../../../components/editors/CodeEditor';
import { InputGroupList } from '../../../components/inputs/InputGroupList';
import InputText from '../../../components/inputs/InputText';
import InputTextArea from '../../../components/inputs/InputTextArea';
import { Container } from '../../../components/layout/ContainerFlex';
import { ScreenTitle } from '../../../components/layout/ScreenTitle';
import TitleSection from '../../../components/layout/TitleSection';
import { ActionBar } from '../../../components/nav/ActionBar';
import PanelContainer from '../../../containers/PanelContainer';
import { datasetAutomationType } from '../../../datasetAutomation/store/datasetAutomation.state';
import { datasetObjectDocument } from '../../../datasetObject/store/datasetObject.state';
import DataSettingSelector from '../../../feed/components/DataSettingSelector';
import {
  DatasetType,
  ImportTypeToJobTypeMap,
} from '../../../import/store/import.types';
import { warehouseSelectedId } from '../../../store/warehouse.state';
import {
  importPipelineIsActiveEditor,
  importPipelineSelectedId,
  importPipelinesDataTableState,
} from '../../store/importPipelines.state';

type PipelineEditableProps = Pick<
  ImportPipelineFragment,
  'title' | 'description' | 'enabled' | 'settings' | 'extraSettings'
>;

type PipelineEditorProps = {
  pipeline: ImportPipelineFragment;
  isNew: boolean;
  onChange: (cnn: ImportPipelineConnectorSettingsFragment) => Promise<void>;
};

type PipelineAppendType = 'new' | 'append' | 'overwrite';

function getAppendType(pipeline: PipelineEditableProps): PipelineAppendType {
  if (_.isNil(pipeline?.settings?.targetSettings?.appendSettings)) {
    return 'new';
  }
  if (pipeline?.settings?.targetSettings?.appendSettings?.overwrite) {
    return 'overwrite';
  }

  return 'append';
}

function getSupportedAppendTypes(
  importType: ImportJobImportType,
): PipelineAppendType[] {
  // Based on the codebase analysis, some types don't support append operations
  const unsupportedAppendTypes = [
    ImportJobImportType.ITEM_SET,
    ImportJobImportType.ASSIGNMENT,
  ];

  if (unsupportedAppendTypes.includes(importType)) {
    return ['new'];
  }

  return ['new', 'append', 'overwrite'];
}

function formatGraphQLError(error: any, t: (key: string) => string): string {
  if (!error) return '';

  // Handle GraphQL error format
  if (error.graphQLErrors?.length > 0) {
    const gqlError = error.graphQLErrors[0];
    const message = gqlError.message;

    // Extract field name from the error message
    const fieldMatch = message.match(/Field "([^"]+)" of required type/);
    if (fieldMatch) {
      const fieldName = fieldMatch[1];
      return t(`Missing required field: ${fieldName}`);
    }

    // Extract variable name from the error message
    const variableMatch = message.match(
      /Variable "\$([^"]+)" got invalid value/,
    );
    if (variableMatch) {
      const variableName = variableMatch[1];
      return t(`Invalid value for ${variableName}`);
    }

    return message;
  }

  // Handle network or other errors
  return error.message || t('An unexpected error occurred');
}

function validatePipelineSettings(
  targetSettings: ImportJobPipelineTargetSettingsInput,
  importType: ImportJobImportType,
  t: (key: string) => string,
): string | null {
  const supportedTypes = getSupportedAppendTypes(importType);

  if (
    targetSettings.target === ImportJobTargetType.APPEND &&
    !supportedTypes.includes('append')
  ) {
    return t('Append operation is not supported for this dataset type');
  }

  if (targetSettings.target === ImportJobTargetType.APPEND) {
    if (!targetSettings.appendSettings?.targetId) {
      return t('Please select a target dataset for append operation');
    }
  } else if (targetSettings.target === ImportJobTargetType.NEW) {
    if (!targetSettings.newSettings?.targetTitle) {
      return t('Please provide a title for the new dataset');
    }
  }
  return null;
}

const PipelineEditor: React.FC<PipelineEditorProps> = props => {
  const { t } = useTranslation('dataset');

  // recoil state
  const warehouseId = useRecoilValue(warehouseSelectedId);
  const setShowEditor = useSetRecoilState(importPipelineIsActiveEditor);
  const datasetObject = useRecoilValue(datasetObjectDocument);
  const automationImportType = useRecoilValue(datasetAutomationType);
  const setSelectedId = useSetRecoilState(importPipelineSelectedId);
  const [tableState, setTableState] = useRecoilState(
    importPipelinesDataTableState,
  );
  const [callCreate] = useCreateImportPipelineMutation();
  const [callUpdate] = useUpdateImportPipelineMutation();

  // local state
  const [pipeline, setPipeline] = useState<PipelineEditableProps>(
    props.pipeline,
  );
  const [jobId, setJobId] = useState<string>(null);
  const [appendType, setAppendType] = useState<PipelineAppendType>(
    getAppendType(pipeline),
  );
  const [targetTitle, setTargetTitle] = useState<string>(
    pipeline?.settings?.targetSettings?.newSettings?.targetTitle ?? '',
  );
  const [targetId, setTargetId] = useState<string>(
    pipeline?.settings?.targetSettings?.appendSettings?.targetId,
  );

  const [query, setQuery] = useState(
    pipeline?.settings?.mappingSettings?.transformation?.query,
  );
  const [isChanged, setIsChanged] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const jobImportType = automationImportType ?? datasetObject?.objectType;
  const importType = _.toPairs(ImportTypeToJobTypeMap).find(
    ([key, value]) => value === jobImportType,
  )?.[0] as DatasetType;

  if (_.isNil(jobImportType)) {
    return <ErrorIndicator message={t`Unknown dataset type`} />;
  }

  const { data: importJobResult, loading } = useLoadImportJobsQuery({
    variables: {
      filter: {
        importType: [jobImportType],
        status: [BatchJobStatus.READY],
        source: [
          ImportSource.UPLOAD,
          ImportSource.DATASET,
          ImportSource.PIPELINE,
        ],
        warehouseId: [warehouseId],
        targetId: datasetObject ? [datasetObject.id] : null,
      },
      // page: null,
      page: { limit: 50 },
      sortBy: {
        field: ImportJobSortOption.UPDATED_AT,
        direction: SortDirection.DESC,
      },
    },
  });

  useEffect(() => {
    setPipeline(
      props.pipeline ?? {
        enabled: true,
      },
    );
    setIsChanged(false);
  }, [props.pipeline]);

  function reloadTable() {
    setTableState({
      ...tableState,
      searchValues: { ...tableState?.searchValues },
    });
  }

  function applyPatch(patch: Partial<ImportPipelineFragment>) {
    setPipeline({ ...pipeline, ...patch });
  }

  function getTargetSettings() {
    const createNew = appendType === 'new';
    const targetSettings: ImportJobPipelineTargetSettingsInput = {
      target: createNew ? ImportJobTargetType.NEW : ImportJobTargetType.APPEND,
      newSettings: createNew
        ? {
            targetTitle,
          }
        : null,
      appendSettings: !createNew
        ? {
            targetId,
            overwrite: appendType === 'overwrite',
          }
        : null,
    };
    return targetSettings;
  }

  const appendOptions: { key: PipelineAppendType; label: string }[] =
    getSupportedAppendTypes(jobImportType).map(type => ({
      key: type,
      label: t(
        type === 'new'
          ? 'Create new'
          : type === 'append'
            ? 'Append'
            : 'Overwrite',
      ),
    }));

  const subtitle = props.isNew ? t`Create pipeline` : t`Update pipeline`;

  if (!pipeline) {
    return null;
  }

  async function updatePipeline() {
    setIsLoading(true);
    setError(null);
    const targetSettings = getTargetSettings();

    // Validate settings before making the API call
    const validationError = validatePipelineSettings(
      targetSettings,
      jobImportType,
      t,
    );
    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      const pipelineSettings: ImportPipelineSettingsInput = job
        ? {
            ...cloneWithoutTypename(pipeline.settings),
            targetSettings,
            sourceSettings: {
              format: job.sourceSettings.format,
              compression: job.sourceSettings.compression,
            },
            mappingSettings: {
              ...cloneWithoutTypename(job.settings),
              transformation: _.isEmpty(query)
                ? null
                : {
                    enabled: true,
                    query,
                  },
            },
          }
        : {
            ...cloneWithoutTypename(pipeline.settings),
            targetSettings,
            mappingSettings: {
              ...cloneWithoutTypename(pipeline.settings.mappingSettings),
              transformation: _.isEmpty(query)
                ? null
                : {
                    enabled: true,
                    query,
                  },
            },
          };

      await callUpdate({
        variables: {
          input: {
            importPipelineId: props.pipeline.id,
            title: pipeline.title,
            description: pipeline.description,
            enabled: pipeline.enabled,
            settings: pipelineSettings,
          },
        },
      });
      reloadTable();
      setShowEditor(false);
    } catch (ex) {
      console.error(ex);
      setError(formatGraphQLError(ex, t));
    } finally {
      setIsLoading(false);
    }
  }

  async function runCreatePipeline() {
    try {
      setIsLoading(true);
      setError(null);

      const job = jobs[jobId];
      const targetSettings = getTargetSettings();

      // Validate settings before making the API call
      const validationError = validatePipelineSettings(
        targetSettings,
        jobImportType,
        t,
      );
      if (validationError) {
        setError(validationError);
        setIsLoading(false);
        return;
      }

      const response = await callCreate({
        variables: {
          input: {
            ...pipeline,
            warehouseId,
            importType: jobImportType,
            settings: {
              targetSettings,
              sourceSettings: {
                format: job.sourceSettings.format,
                compression: job.sourceSettings.compression,
              },
              mappingSettings: {
                ...cloneWithoutTypename(job.settings),
                transformation: _.isEmpty(query)
                  ? null
                  : {
                      enabled: true,
                      query,
                    },
              },
            },
          },
        },
      });

      setSelectedId(response?.data?.createImportPipeline?.id);
      reloadTable();
      setShowEditor(false);
    } catch (ex) {
      console.error(ex);
      setError(formatGraphQLError(ex, t));
    } finally {
      setIsLoading(false);
    }
  }

  const jobs = _(importJobResult?.importJobs?.content)
    .keyBy(j => j.id)
    .value();

  const job = jobs?.[jobId];

  return (
    <Container col hasOverflowY>
      <ScreenTitle subtitle={subtitle} title={t`Pipeline`} isSticky />
      <PanelContainer id="panel-pipeline-settings" title={t`Settings`}>
        {!_.isNil(error) && (
          <ErrorIndicator
            title={t`Check Pipeline Settings`}
            showHelperMessage={false}
            message={error}
            className="mt-2"
          />
        )}

        <InputGroupList hasPadding hasSpacing panelMode className="px-4 py-4">
          <InputText
            required
            placeholder={t`Pipeline Name`}
            label={t`Pipeline Name`}
            name={t`Pipeline Name`}
            value={pipeline?.title}
            onChange={v => applyPatch({ title: v })}
          />
          <InputTextArea
            optional
            label={t`Pipeline Description`}
            placeholder={t`About the Pipeline...`}
            value={pipeline.description}
            onChange={v => applyPatch({ description: v })}
          />
          <DropdownSelector
            label={t`Import Template`}
            value={jobId}
            values={_.keys(jobs)}
            renderValue={v =>
              _.isNil(v)
                ? t`No Job Template selected`
                : (jobs[v]?.sourceSettings?.filename ?? '')
            }
            valueHelper={v =>
              v && jobs[v] ? formatDateTime(new Date(jobs[v].createdAt)) : ''
            }
            onChange={v => {
              const job = jobs[v];
              setJobId(v);
              setQuery(job.settings.transformation?.query ?? '');
            }}
            panelModeMultiline
            DropAlignRight
            widthFull
            panelMode
            hasSearch
            hasSearchLabel={t`Import jobs`}
          />

          <ButtonSwitchMulti
            autoSize
            className={classNames('mx-3')}
            classNameLabel={classNames('text-xs py-1 px-2')}
            buttonType="secondary"
            options={appendOptions}
            selectedIndex={_.findIndex(
              appendOptions,
              o => o.key === appendType,
            )}
            onClick={index => setAppendType(appendOptions[index].key)}
          />
          {appendType === 'new' ? (
            <InputText
              required
              placeholder={t`New Dataset Name Prefix`}
              label={t`New Dataset Name Prefix`}
              name={t`New Dataset Name Prefix`}
              value={targetTitle}
              onChange={v => setTargetTitle(v)}
              // isDisabled={appendType === 'append'}
            />
          ) : (
            <DataSettingSelector
              dataType={importType}
              datasetId={targetId}
              onChange={id => setTargetId(id)}
              viewMode="minimal"
            />
          )}

          {!props.isNew && (
            <DropdownSelector
              label={t`Status`}
              value={pipeline.enabled}
              values={[false, true]}
              renderValue={v => (v ? t`Enabled` : t`Disabled`)}
              onChange={v => applyPatch({ enabled: v })}
              panelModeMultiline
              DropAlignRight
              widthFull
              panelMode
            />
          )}
        </InputGroupList>
        {!_.isNil(query) && (
          <TitleSection
            id={`job-settings-query`}
            title={t`Settings: Transformation`}
            inPanelView
            hasScreenTitle
            collapsible
          >
            <CodeEditor
              value={query}
              language={'trinosql'}
              // readOnly
              height={`${(query.split('\n').length || 1) * 28}px`}
              minHeight="42px"
              className="flex-1"
              editorOptions={{
                scrollBeyondLastLine: false,
                overviewRulerBorder: false,
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                lineDecorationsWidth: 10,
                scrollbar: {
                  vertical: 'hidden',
                  horizontal: 'hidden',
                  handleMouseWheel: false,
                },
                guides: {
                  indentation: false,
                  highlightActiveIndentation: true,
                  // bracketPairs: false,
                },
                fontSize: 12,
                lineNumbers: 'on',
                folding: true,
                tabSize: 2,
                padding: { top: 10, bottom: 10 },
              }}
              onChange={v => setQuery(v)}
            />
          </TitleSection>
        )}
      </PanelContainer>
      <ActionBar stickyBottom>
        <Button
          className={classNames('flex-1 text-sm')}
          label={t`Cancel`}
          buttonType="secondary"
          onPress={() => setShowEditor(false)}
          isDisabled={isLoading}
          isLoading={isLoading}
        />
        <Button
          className={classNames('flex-1 text-sm')}
          label={props.isNew ? t`Create` : t`Update`}
          buttonType="primary"
          onPress={props.isNew ? runCreatePipeline : updatePipeline}
          isDisabled={isLoading || (_.isNil(jobId) && props.isNew)}
          isLoading={isLoading}
        />
      </ActionBar>
    </Container>
  );
};

export default PipelineEditor;
